import I18n from 'I18n';
import FormattedHTMLMessage from 'I18n/components/FormattedHTMLMessage';
import FormattedJSXMessage from 'I18n/components/FormattedJSXMessage';
import FormattedMessage from 'I18n/components/FormattedMessage';
import formatName from 'I18n/utils/formatName';
import PortalIdParser from 'PortalIdParser';
import { useMutation } from 'data-fetching-client';
import { Text } from 'foundations-components/components/Text';
import { Button } from 'foundations-components/transitional/components/Button';
import { Checkbox } from 'foundations-components/transitional/components/Checkbox';
import { DialogBody } from 'foundations-components/transitional/components/DialogBody';
import { DialogCloseButton } from 'foundations-components/transitional/components/DialogCloseButton';
import { DialogFooter } from 'foundations-components/transitional/components/DialogFooter';
import { DialogHeader } from 'foundations-components/transitional/components/DialogHeader';
import { FloatingAlertStore } from 'foundations-components/transitional/components/FloatingAlertList';
import { FormControl } from 'foundations-components/transitional/components/FormControl';
import { H2 } from 'foundations-components/transitional/components/H2';
import { Link } from 'foundations-components/transitional/components/Link';
import { LoadingButton } from 'foundations-components/transitional/components/LoadingButton';
import { Modal } from 'foundations-components/transitional/components/Modal';
import { Paragraph } from 'foundations-components/transitional/components/Paragraph';
import { RadioInput } from 'foundations-components/transitional/components/RadioInput';
import { Small } from 'foundations-components/transitional/components/Small';
import { ToggleGroup } from 'foundations-components/transitional/components/ToggleGroup';
import { Tooltip } from 'foundations-components/transitional/components/Tooltip';
import { useCallback, useState } from 'react';
import { useDetailsPageContext } from '../context/DetailsPageContext';
import { ENROLL_BETA } from './queries/betaEnrollmentQueries';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const ENROLL_TYPE = 'PORTALS_ONLY';
const JoinPublicBetaModal = ({
  rolloutId,
  title,
  onSuccessfulJoin,
  onClose
}) => {
  var _userInfo$user;
  const portalId = PortalIdParser.get();
  const {
    trackInteraction,
    userInfo
  } = useDetailsPageContext();
  const [isTermsAcknowledged, setIsTermsAcknowledged] = useState(false);
  const [joinBeta, {
    loading,
    error
  }] = useMutation(ENROLL_BETA, {});
  const userId = userInfo === null || userInfo === void 0 ? void 0 : userInfo.user;
  const handleJoin = useCallback(() => {
    if (!portalId || !userId) {
      FloatingAlertStore.addAlert({
        type: 'error',
        message: I18n.text('detailsPage.productDetailsCard.cta.joinBetaModal.problemJoiningAccount')
      });
      return;
    }
    joinBeta({
      variables: {
        rolloutId,
        portalId,
        enrollId: portalId,
        enrollType: ENROLL_TYPE,
        betaAccessType: 'PUBLIC'
      }
    }).then(() => {
      trackInteraction({
        action: 'beta joined',
        section: 'join public beta modal'
      });
      FloatingAlertStore.addAlert({
        type: 'success',
        message: I18n.text('detailsPage.productDetailsCard.cta.joinBetaModal.accountJoined', {
          betaName: title
        })
      });
      if (onSuccessfulJoin) {
        onSuccessfulJoin();
      }
    }).catch(() => {
      FloatingAlertStore.addAlert({
        type: 'error',
        message: I18n.text('detailsPage.productDetailsCard.cta.joinBetaModal.problemJoiningAccount')
      });
    }).finally(onClose);
  }, [portalId, userId, joinBeta, rolloutId, onClose, trackInteraction, title, onSuccessfulJoin]);
  return /*#__PURE__*/_jsxs(Modal, {
    use: "success",
    children: [/*#__PURE__*/_jsxs(DialogHeader, {
      children: [/*#__PURE__*/_jsx(H2, {
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "detailsPage.productDetailsCard.cta.joinBetaModal.header"
        })
      }), /*#__PURE__*/_jsx(DialogCloseButton, {
        onClick: onClose
      })]
    }), /*#__PURE__*/_jsxs(DialogBody, {
      children: [/*#__PURE__*/_jsx(Paragraph, {
        children: /*#__PURE__*/_jsx(FormattedHTMLMessage, {
          message: "detailsPage.productDetailsCard.cta.joinBetaModal.description",
          options: {
            betaName: title
          }
        })
      }), /*#__PURE__*/_jsxs(ToggleGroup, {
        name: "beta-target",
        marginBlockStart: "600",
        children: [/*#__PURE__*/_jsx(Tooltip, {
          placement: "bottom",
          title: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "detailsPage.productDetailsCard.cta.joinBetaModal.disabledOption"
          }),
          disabled: false,
          children: /*#__PURE__*/_jsx(RadioInput, {
            "data-test-id": "beta-user-radio",
            checked: false,
            disabled: true //This option is not yet supported for marketplace public betas
            ,
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "detailsPage.productDetailsCard.cta.joinBetaModal.adminOption",
              options: {
                name: formatName({
                  email: userInfo === null || userInfo === void 0 || (_userInfo$user = userInfo.user) === null || _userInfo$user === void 0 ? void 0 : _userInfo$user.email
                })
              }
            })
          })
        }), /*#__PURE__*/_jsx(RadioInput, {
          "data-test-id": "beta-portal-radio",
          checked: true,
          disabled: false,
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "detailsPage.productDetailsCard.cta.joinBetaModal.enrollPortalOption",
            options: {
              portalId: I18n.SafeString(portalId.toString())
            }
          })
        }), /*#__PURE__*/_jsx(Tooltip, {
          disabled: false,
          placement: "bottom",
          title: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "detailsPage.productDetailsCard.cta.joinBetaModal.disabledOption"
          }),
          children: /*#__PURE__*/_jsx(RadioInput, {
            "data-test-id": "beta-organization-radio",
            checked: false,
            disabled: true //This option is not yet supported for marketplace public betas
            ,
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "detailsPage.productDetailsCard.cta.joinBetaModal.organizationOption"
            })
          })
        })]
      }), /*#__PURE__*/_jsx(FormControl, {
        marginBlock: "500",
        children: /*#__PURE__*/_jsx(Checkbox, {
          checked: isTermsAcknowledged,
          onChange: ({
            target: {
              checked
            }
          }) => {
            if (checked) {
              trackInteraction({
                action: 'acknowledge beta terms',
                section: 'join public beta modal'
              });
            }
            setIsTermsAcknowledged(checked);
          },
          children: /*#__PURE__*/_jsx(Text, {
            children: /*#__PURE__*/_jsx(FormattedJSXMessage, {
              message: "detailsPage.productDetailsCard.cta.joinBetaModal.legalAcknowledgement_jsx",
              options: {
                href: 'https://legal.hubspot.com/hubspot-beta-terms',
                external: true
              },
              elements: {
                Link
              }
            })
          })
        })
      }), /*#__PURE__*/_jsx(Small, {
        use: "help",
        children: /*#__PURE__*/_jsx("b", {
          children: /*#__PURE__*/_jsx(FormattedHTMLMessage, {
            message: "detailsPage.productDetailsCard.cta.joinBetaModal.enrollmentNote"
          })
        })
      })]
    }), /*#__PURE__*/_jsxs(DialogFooter, {
      children: [/*#__PURE__*/_jsx(LoadingButton, {
        "data-test-id": "join-beta-modal-accept-btn",
        disabled: !isTermsAcknowledged,
        failed: Boolean(error),
        loading: loading,
        onClick: handleJoin,
        use: "primary",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "detailsPage.productDetailsCard.cta.joinBetaAndAdd"
        })
      }), /*#__PURE__*/_jsx(Button, {
        onClick: onClose,
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "detailsPage.productDetailsCard.cta.joinBetaModal.noThanks"
        })
      })]
    })]
  });
};
export { JoinPublicBetaModal };